import { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, ShieldCheck, CheckCircle2, Loader2 } from 'lucide-react';
import { submitLead, trackLead, waLink } from '../lib/lead';

export const INDIAN_STATES = [
  'Andhra Pradesh', 'Assam', 'Bihar', 'Chandigarh', 'Chhattisgarh', 'Delhi', 'Goa', 'Gujarat',
  'Haryana', 'Himachal Pradesh', 'Jammu & Kashmir', 'Jharkhand', 'Karnataka', 'Kerala',
  'Madhya Pradesh', 'Maharashtra', 'Odisha', 'Puducherry', 'Punjab', 'Rajasthan', 'Sikkim',
  'Tamil Nadu', 'Telangana', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal', 'Other State / UT',
];

const inputCls =
  'w-full h-12 rounded-xl border border-slate-200 bg-slate-50/60 px-4 text-sm font-medium text-brand-ink placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-orange/60 focus:border-brand-orange transition-all';

export default function LeadForm({ variant = 'hero', source = 'hero' }) {
  const isHero = variant === 'hero';
  const [form, setForm] = useState({ full_name: '', vehicle_number: '', state_city: '', phone: '' });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');
    const digits = form.phone.replace(/\D/g, '').slice(-10);
    if (digits.length !== 10 || !'6789'.includes(digits[0])) {
      setError('Please enter a valid 10-digit WhatsApp mobile number.');
      return;
    }
    if (!isHero && !form.full_name.trim()) {
      setError('Please enter your name.');
      return;
    }
    const payload = {
      full_name: form.full_name.trim() || null,
      phone: digits,
      vehicle_number: form.vehicle_number.trim().toUpperCase() || null,
      state_city: form.state_city || null,
      source,
    };
    trackLead(source);
    const msg = isHero
      ? `Hi Settalkar, I want to settle my traffic challan.\nVehicle No: ${payload.vehicle_number || '—'}\nState/City: ${payload.state_city || '—'}\nMy WhatsApp: +91 ${digits}`
      : `Hi Settalkar, I need an urgent legal callback.\nName: ${payload.full_name}\nWhatsApp: +91 ${digits}\nVehicle No: ${payload.vehicle_number || '—'}\nCity: ${payload.state_city || '—'}`;
    window.open(waLink(msg), '_blank', 'noopener');
    setSending(true);
    submitLead(payload)
      .then(() => setDone(true))
      .catch(() => setDone(true))
      .finally(() => setSending(false));
  };

  if (done) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        className="flex flex-col items-center text-center py-10 px-6"
        data-testid={`${variant}-lead-success`}
      >
        <span className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-50 border-2 border-green-200 mb-5">
          <CheckCircle2 size={32} className="text-green-600" />
        </span>
        <h3 className="font-display text-2xl font-bold text-brand-ink">Request received</h3>
        <p className="mt-2 text-sm leading-relaxed text-slate-500 max-w-xs">
          Our legal desk will WhatsApp your free settlement quote within minutes. A WhatsApp chat has opened for you —
          press send to get an instant reply.
        </p>
        <button
          onClick={() => setDone(false)}
          className="mt-6 text-sm font-bold text-brand-blue hover:text-brand-orange transition-colors"
          data-testid={`${variant}-lead-reset`}
        >
          Submit another challan
        </button>
      </motion.div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4" data-testid={`${variant}-lead-form`} noValidate>
      {!isHero && (
        <div>
          <label htmlFor={`${variant}-name`} className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5">
            Full Name
          </label>
          <input
            id={`${variant}-name`}
            type="text"
            value={form.full_name}
            onChange={set('full_name')}
            placeholder="Your full name"
            className={inputCls}
            data-testid={`${variant}-input-name`}
          />
        </div>
      )}

      <div>
        <label htmlFor={`${variant}-vehicle`} className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5">
          Vehicle Registration Number
        </label>
        <input
          id={`${variant}-vehicle`}
          type="text"
          value={form.vehicle_number}
          onChange={set('vehicle_number')}
          placeholder="e.g. DL 01 AB 1234 / UP 16 XX 0000"
          className={`${inputCls} uppercase`}
          data-testid={`${variant}-input-vehicle-number`}
          required
        />
      </div>

      <div>
        <label htmlFor={`${variant}-state`} className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5">
          State / City
        </label>
        <select
          id={`${variant}-state`}
          value={form.state_city}
          onChange={set('state_city')}
          className={`${inputCls} ${form.state_city ? '' : 'text-slate-400'}`}
          data-testid={`${variant}-select-state`}
          required
        >
          <option value="" disabled>
            Select your state
          </option>
          {INDIAN_STATES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label htmlFor={`${variant}-phone`} className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5">
          Mobile Number (WhatsApp)
        </label>
        <div className="relative">
          <span className="absolute left-4 top-1/2 -translate-y-1/2 text-sm font-bold text-slate-500 pointer-events-none">+91</span>
          <input
            id={`${variant}-phone`}
            type="tel"
            inputMode="numeric"
            maxLength={10}
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/\D/g, '').slice(0, 10) })}
            placeholder="98765 43210"
            className={`${inputCls} pl-12`}
            data-testid={`${variant}-input-mobile`}
            required
          />
        </div>
      </div>

      {error && (
        <p className="text-xs font-semibold text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2" data-testid={`${variant}-form-error`}>
          {error}
        </p>
      )}

      <button
        type="submit"
        disabled={sending}
        className="group relative w-full h-[52px] rounded-xl bg-brand-orange hover:bg-brand-orangedark disabled:opacity-70 text-white font-bold text-base inline-flex items-center justify-center gap-2 shadow-xl shadow-orange-500/30 transition-all hover:-translate-y-0.5 active:translate-y-0"
        data-testid={`${variant}-submit-button`}
      >
        {sending ? (
          <>
            <Loader2 size={18} className="animate-spin" /> Sending…
          </>
        ) : (
          <>
            {isHero ? 'Get Free Challan Settlement Quote' : 'Request Urgent Legal Callback'}
            <ArrowRight size={18} strokeWidth={2.5} className="transition-transform group-hover:translate-x-1" />
          </>
        )}
      </button>

      <p className="flex items-center justify-center gap-1.5 text-[11px] font-semibold text-slate-500" data-testid={`${variant}-trust-microcopy`}>
        <ShieldCheck size={14} className="text-brand-blue shrink-0" />
        100% Legal &amp; Confidential&nbsp;|&nbsp;0 Court Visits Required
      </p>
    </form>
  );
}
